import type { Integration, SourceConfig, SourceConfigCreate, SourceConfigUpdate } from 'dico-types';
import { create } from 'zustand';
import { integrationApi } from '@/lib/api/integration/integrationApi';

type LoadState = 'idle' | 'loading' | 'loaded' | 'error';

interface IntegrationStoreState {
  accounts: Integration[];
  sourcesByAccount: Record<string, SourceConfig[]>;
  loadState: LoadState;

  load: (workspaceId: string) => Promise<void>;
  loadSources: (workspaceId: string, accountId: string) => Promise<void>;
  removeAccount: (workspaceId: string, accountId: string) => Promise<void>;
  addSource: (
    workspaceId: string,
    accountId: string,
    data: SourceConfigCreate
  ) => Promise<SourceConfig>;
  updateSource: (
    workspaceId: string,
    accountId: string,
    sourceId: string,
    data: SourceConfigUpdate
  ) => Promise<SourceConfig>;
  removeSource: (workspaceId: string, accountId: string, sourceId: string) => Promise<void>;
  reset: () => void;
}

export const useIntegrationStore = create<IntegrationStoreState>((set) => ({
  accounts: [],
  sourcesByAccount: {},
  loadState: 'idle',

  load: async (workspaceId) => {
    set({ loadState: 'loading' });
    try {
      const res = await integrationApi.listAccounts(workspaceId);
      set({ accounts: res.data ?? [], loadState: 'loaded' });
    } catch {
      set({ loadState: 'error' });
    }
  },

  loadSources: async (workspaceId, accountId) => {
    const res = await integrationApi.listSources(workspaceId, accountId);
    if (res.data) {
      set((s) => ({
        sourcesByAccount: { ...s.sourcesByAccount, [accountId]: res.data! }
      }));
    }
  },

  removeAccount: async (workspaceId, accountId) => {
    await integrationApi.removeAccount(workspaceId, accountId);
    set((s) => ({
      accounts: s.accounts.filter((a) => a.id !== accountId),
      sourcesByAccount: Object.fromEntries(
        Object.entries(s.sourcesByAccount).filter(([k]) => k !== accountId)
      )
    }));
  },

  addSource: async (workspaceId, accountId, data) => {
    const res = await integrationApi.addSource(workspaceId, accountId, data);
    if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to add source');
    const source = res.data;
    set((s) => ({
      sourcesByAccount: {
        ...s.sourcesByAccount,
        [accountId]: [...(s.sourcesByAccount[accountId] ?? []), source]
      }
    }));
    return source;
  },

  updateSource: async (workspaceId, accountId, sourceId, data) => {
    const res = await integrationApi.updateSource(workspaceId, accountId, sourceId, data);
    if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to update source');
    const updated = res.data;
    set((s) => ({
      sourcesByAccount: {
        ...s.sourcesByAccount,
        [accountId]: (s.sourcesByAccount[accountId] ?? []).map((src) =>
          src.id === sourceId ? updated : src
        )
      }
    }));
    return updated;
  },

  removeSource: async (workspaceId, accountId, sourceId) => {
    await integrationApi.removeSource(workspaceId, accountId, sourceId);
    set((s) => ({
      sourcesByAccount: {
        ...s.sourcesByAccount,
        [accountId]: (s.sourcesByAccount[accountId] ?? []).filter((src) => src.id !== sourceId)
      }
    }));
  },

  reset: () => set({ accounts: [], sourcesByAccount: {}, loadState: 'idle' })
}));
